import type { FC } from 'react'
import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Loader from '@segment/matcha/components/Loader'

import NavigationV2 from '@segment/templates/Navigation2'
import Footer from '@segment/templates/Footer/Footer'

import { useFiltersQuery } from '@segment/api/routes/useCases/queries'
import type { GroupedFilters } from '@segment/api/routes/useCases/types'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import Filters from './components/Filters'

import { useFilters } from './hooks/useFilters'
import UseCasesGrid from './components/UseCasesGrid'
import GettingStarted from './components/GettingStarted'

import { useUseCasesGrid } from './hooks/useUseCasesGrid'

const copy = {
  pageTitle: `Twilio Segment Use Cases`,
  pageDescription: `Explore top use cases for Twilio Segment and the tools that power them. Implement Segment once, integrate 250+ tools with the flip of a switch, and unlock thousands of new uses cases for marketing, personalization, analytics, and more.`,
}

const UseCasesView: FC = () => {
  const theme = useTheme()
  const { data: filters } = useFiltersQuery({ args: [] })
  const groupedFilterEntries = Object.entries((filters as GroupedFilters) || {})
  const { filtersState, selectHandlers } = useFilters(groupedFilterEntries)
  const { pages, nextPageTrigger, isLoading, hasNextPage, isFetchingNextPage } = useUseCasesGrid(filtersState)

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={copy.pageTitle}
        pageDescription={copy.pageDescription}
        bodyBackground={theme.palette.neutrals.white[100]}
        themeColor={theme.palette.neutrals.white[100]}
      >
        <NavigationV2 />
        <Hero />
        <Main>
          <Filters
            filters={groupedFilterEntries}
            onFilterChange={(filterId, selectedOptions) => selectHandlers[filterId](selectedOptions)}
          />
          <Wrapper>
            {!isLoading && <UseCasesGrid useCases={pages} />}
            {isLoading && (
              <LoaderWrapper>
                <Loader />
              </LoaderWrapper>
            )}
            {hasNextPage && (
              <LoaderWrapper compensateTopGutter={hasNextPage}>{isFetchingNextPage && <Loader />}</LoaderWrapper>
            )}
            {nextPageTrigger}
          </Wrapper>
          {!hasNextPage && <GettingStarted />}
        </Main>
        {!hasNextPage && <Footer />}
      </Layout>
    </AppearanceProvider>
  )
}

export default UseCasesView

const Main = styled.main``

const Wrapper = styled.div``

interface LoaderWrapperProps {
  compensateTopGutter?: boolean
}
const LoaderWrapper = styled.div<LoaderWrapperProps>`
  display: grid;
  align-items: center;
  justify-content: center;
  height: 400px;
  padding: 80px 0;
  margin: 0 auto;
  ${({ compensateTopGutter }) =>
    compensateTopGutter &&
    css`
      padding: 0;
      margin-top: -160px;
    `}
`
